import type { View } from "@novorender/api";
import type { HierarcicalObjectReference, ObjectData, ObjectId } from "@novorender/data-js-api";
import type { Predicate } from "@procore/bim-index";

import {
    type BoundsOptions,
    byProperty,
    type FindIdsOptions,
    type ObjectDataAccess,
    type PropertyQuery,
    type SelectionBounds,
} from "dataAccess";
import { NodeType } from "types/misc";
import { log } from "utils/log";

import { queryPredicate } from "./predicate";
import type { Requests } from "./protocol";
import { bimIndexClient, ensureBimIndexOpen } from "./session";

/** How many tree rows to pull per worker call while an iterator is consumed. */
const PAGE_SIZE = 200;

async function* emptyIterator(): AsyncIterableIterator<HierarcicalObjectReference> {}

/**
 * Data access backed by a BIM index **already downloaded to this device**.
 *
 * *Offline* is the whole point of it, and is why it is in the name: the index files
 * sit in OPFS, the WASM engine reads them through sync access handles in a worker,
 * and every query — hierarchy, properties, search — is answered in-process with no
 * request per node and no network at all. It is chosen only for a scene that has the
 * files (see `availability.ts`), and a scene that does not falls back to the object
 * DB rather than fetching them.
 *
 * A *served* BIM index is the sibling to expect: same index, same query algebra, but
 * queried over the network instead, and so available to every scene rather than only
 * a downloaded one. It is a separate implementation because the two differ in exactly
 * what the interface asks about — `isIndexAvailableOffline` is unconditionally `true`
 * here and would be `false` there — not because they answer differently.
 *
 * Object *properties* are the one thing the index cannot provide: it is an inverted
 * index with no forward per-object record, and the native reader that has one seeks
 * into the original source document, which the browser build cannot open. Those come
 * from the scene's own metadata (`view.data`) instead, which keeps this backend free
 * of the network — and answers `undefined` for objects when a scene carries no
 * metadata, rather than reaching for a server.
 *
 * The index loads on the first call that needs it rather than at construction, so
 * choosing this implementation at startup costs nothing until it is asked something.
 * `openParams` names the OPFS folder the index files sit in — the worker only ever
 * reads what someone else has already downloaded there.
 */
export class BimIndexOfflineDataAccess implements ObjectDataAccess {
    readonly kind = "bim-index-offline" as const;

    constructor(
        private readonly view: View,
        private readonly openParams: Requests["open"]["params"],
    ) {}

    isIndexAvailableOffline() {
        return true;
    }

    isDataAvailableOffline() {
        return Boolean(this.view.data);
    }

    /**
     * Straight out of the scene's metadata. A `MetaDataEntry` is an `ObjectData`
     * without its `loadMetaData`/`save` methods, which no consumer of this call uses —
     * the same cast `utils/search.getObjectData` makes on this path.
     */
    async getObject(id: ObjectId): Promise<ObjectData | undefined> {
        try {
            return (await this.view.data?.getObjectMetaData(id)) as ObjectData | undefined;
        } catch {
            // A missing or unreadable entry is "no object", as on the db path.
            return undefined;
        }
    }

    /**
     * The index resolves the path locally (`$path` is hash-indexed, so this is an
     * equality lookup rather than a scan); the object it names comes from the scene
     * metadata, since the index holds no properties.
     */
    async getObjectAtPath(path: string, signal?: AbortSignal): Promise<ObjectData | undefined> {
        await this.open();
        if (signal?.aborted) {
            return undefined;
        }
        const id = await bimIndexClient().call("objectIdAtPath", { path });
        return id === null ? undefined : this.getObject(id);
    }

    /**
     * By id, straight off the index's parent map — no path arithmetic and no request.
     * The parent object itself comes from the scene metadata.
     */
    async getParent(object: HierarcicalObjectReference, signal?: AbortSignal): Promise<ObjectData | undefined> {
        await this.open();
        if (signal?.aborted) {
            return undefined;
        }
        const parentId = await bimIndexClient().call("parentId", { id: object.id });
        return parentId === null ? undefined : this.getObject(parentId);
    }

    /**
     * The index's tree is id-keyed, and the object carries its id. Pages are pulled
     * as the iterator is consumed, so its cursor never leaves this class and a caller
     * that stops early stops the work.
     */
    async *getChildren(
        parent: HierarcicalObjectReference | undefined,
        signal?: AbortSignal,
    ): AsyncIterableIterator<HierarcicalObjectReference> {
        await this.open();
        for (let cursor: string | null = null; !signal?.aborted; ) {
            // Annotated because `cursor` is both an input to and an output of this
            // call, which otherwise makes the inference circular.
            const page: Requests["modelTree"]["result"] = await bimIndexClient().call("modelTree", {
                id: parent?.id ?? null,
                limit: PAGE_SIZE,
                cursor,
            });
            for (const node of page.items) {
                yield this.reference(node.id, node.path, node.hasChildren);
            }
            cursor = page.next_cursor;
            if (!cursor) {
                return;
            }
        }
    }

    /** One predicate over the query's terms, paged as the iterator is consumed. */
    findByProperties(query: PropertyQuery, signal?: AbortSignal): AsyncIterableIterator<HierarcicalObjectReference> {
        const predicate = queryPredicate(query);
        return predicate ? this.matches(predicate, signal) : emptyIterator();
    }

    /**
     * One worker call: the engine's `expand` is the same `ExpandAsync` primitive the
     * served backend reaches over `objects/expand`, and the match bitmap feeds it
     * without leaving WASM — so no rows are built and no intermediate ids materialized.
     *
     * Nothing here reads a node type. `expand` unions each root's *self-inclusive*
     * subtree, so a leaf contributes itself and the matches need no merging back in.
     */
    async findIdsByProperties(query: PropertyQuery, options?: FindIdsOptions): Promise<Uint32Array> {
        const { includeDescendants = false, signal } = options ?? {};
        const predicate = queryPredicate(query);
        if (!predicate) {
            return new Uint32Array();
        }

        await this.open();
        if (signal?.aborted) {
            return new Uint32Array();
        }

        const { ids, degraded } = await bimIndexClient().call("matchIds", { predicate, includeDescendants });
        if (degraded) {
            log.dataAccess.bimIndex.warn("the engine hit its scan deadline; ids may be incomplete");
        }
        return signal?.aborted ? new Uint32Array() : ids;
    }

    /**
     * `CONTAINS $name` — substring on the name column, answered by the engine.
     *
     * This is the operator that means what the method does: every row it returns is a
     * name match, so there is nothing to filter here and nothing to scan past. It also
     * matches mid-token, so "acade" finds "Facade" the way the object DB does.
     *
     * The cost is that `CONTAINS` is not an accelerated column the way `SEARCH` is —
     * it is a scan over the name dictionary — so a broad term on a large model can hit
     * the engine's query timeout and come back silently partial (measured on the sample
     * project: `"wal"` returned fewer matches than `"wall"`). That trade is deliberate:
     * a search box would rather answer the right question incompletely than a
     * token-prefix question exactly.
     *
     * NOTE: SEARCH operator currently doesn't search on object name.
     */
    searchByName(term: string, signal?: AbortSignal): AsyncIterableIterator<HierarcicalObjectReference> {
        const needle = term.trim();
        if (!needle) {
            return emptyIterator();
        }
        // `case_sensitive` is spelled out rather than left to the engine's default: the
        // search box has always been case-insensitive, and the flag is optional in the
        // predicate type with no documented default.
        return this.matches({ operator: "CONTAINS", property: "$name", value: needle, case_sensitive: false }, signal);
    }

    /** Tree rows for a predicate, paged as the iterator is consumed. */
    private async *matches(
        predicate: Predicate,
        signal?: AbortSignal,
    ): AsyncIterableIterator<HierarcicalObjectReference> {
        await this.open();
        for (let offset = 0; !signal?.aborted; offset += PAGE_SIZE) {
            const page: Requests["matchRows"]["result"] = await bimIndexClient().call("matchRows", {
                predicate,
                offset,
                limit: PAGE_SIZE,
            });
            for (const row of page.items) {
                yield this.reference(row.id, row.path, row.hasChildren);
            }
            if (!page.items.length || offset + page.items.length >= page.total) {
                return;
            }
        }
    }

    /**
     * The whole chain in one call, walked over the index's parent map — no upward
     * search, and nothing to de-duplicate: an id has exactly one parent, so the chain
     * comes back complete rather than reconstructed from a stream.
     */
    async getAncestors(
        object: HierarcicalObjectReference,
        signal?: AbortSignal,
    ): Promise<HierarcicalObjectReference[]> {
        await this.open();
        if (signal?.aborted) {
            return [];
        }
        const chain = await bimIndexClient().call("ancestors", { id: object.id });
        // Every ancestor has children by definition — it has this object beneath it.
        return chain.map((node) => this.reference(node.id, node.path, true));
    }

    /**
     * One bitmap out of the index's object tree, materialized as ids and transferred
     * from the worker rather than copied.
     */
    async getDescendantIds(object: HierarcicalObjectReference, signal?: AbortSignal): Promise<Uint32Array> {
        await this.open();
        if (signal?.aborted) {
            return new Uint32Array();
        }
        return bimIndexClient().call("descendantIds", { id: object.id });
    }

    /**
     * Nothing, deliberately — `false` says the ids are not on the object, and a caller
     * that actually needs them asks {@link getDescendantIds}.
     *
     * We already have the descendants available locally.
     * They are in OPFS, not in memory yet, but still warming up sounds like a waste of memory here.
     */
    warmDescendantIds(_object: HierarcicalObjectReference, _signal?: AbortSignal): Promise<boolean> {
        return Promise.resolve(false);
    }

    /**
     * One worker call: the engine folds the volume off its bounds table for the whole
     * match set, so a selection of any size costs the same as one object and
     * nothing but the answer crosses back — the served backend reaches the very same
     * primitive over `objects/bounds`.
     *
     * The ids become a `$id IN` term, which `predicate.ts` already chunks into `OR`ed
     * 500-value branches, so a selection of hundreds of thousands stays one query.
     */
    async getBounds(ids: Iterable<ObjectId>, options?: BoundsOptions): Promise<SelectionBounds | undefined> {
        const { parts = "sphere", signal } = options ?? {};
        const idArr = Array.isArray(ids) ? ids : Array.from(ids);
        if (!idArr.length) {
            return undefined;
        }
        const predicate = queryPredicate(byProperty("id", idArr.map(String)));
        if (!predicate) {
            return undefined;
        }

        await this.open();
        if (signal?.aborted) {
            return undefined;
        }

        const bounds = await bimIndexClient().call("bounds", { predicate, parts });
        if (!bounds || signal?.aborted) {
            return undefined;
        }
        return {
            ...(bounds.box ? { box: bounds.box } : {}),
            ...(bounds.sphere ? { sphere: bounds.sphere } : {}),
        };
    }

    /**
     * A tree row as the rest of the app expects one. Everything but the properties
     * comes from the index; `loadMetaData` reaches for those through the same scene
     * metadata `getObject` reads — so a caller can open any row without knowing where
     * each half came from.
     */
    private reference(id: ObjectId, path: string, hasChildren: boolean): HierarcicalObjectReference {
        const loadObject = () => this.getObject(id);
        return {
            id,
            path,
            // `NodeType` in the API is an ambient const enum, which `isolatedModules`
            // cannot read; `types/misc` mirrors its values (see the note there).
            type: (hasChildren ? NodeType.Internal : NodeType.Leaf) as HierarcicalObjectReference["type"],
            async loadMetaData(): Promise<ObjectData> {
                const object = await loadObject();
                if (!object) {
                    throw new Error(`Object ${id} has no metadata available`);
                }
                return object;
            },
        };
    }

    private open() {
        return ensureBimIndexOpen(this.openParams);
    }
}
